import {
  type TiptapFileSrcResolver,
  TiptapFileSrcProvider,
  useTiptapFileSrcResolverFromContext,
} from '@duke-hq/ui/components/DukeTiptapEditor/useTiptapFileSrc';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useRef } from 'react';

type AddFileUrl = (fileDrn: string, url: string) => void;

const AddFileUrlContext = createContext<AddFileUrl>(() => {});

/**
 * Resolves file nodes under it from `fileUrls`, the URL map a query returned
 * beside its content, plus the URLs of files uploaded while it is mounted.
 */
export function FileUrlsProvider({
  fileUrls,
  children,
}: {
  fileUrls: Record<string, string> | undefined;
  children: ReactNode;
}) {
  // A ref, not state: the editor renders the uploaded node straight after the upload resolves, before any re-render.
  const uploadedFileUrlsRef = useRef<Record<string, string>>({});
  const addFileUrl = useCallback<AddFileUrl>((fileDrn, url) => {
    uploadedFileUrlsRef.current = { ...uploadedFileUrlsRef.current, [fileDrn]: url };
  }, []);
  const resolver = useTiptapFileSrcResolver(fileUrls, uploadedFileUrlsRef);

  return (
    <AddFileUrlContext.Provider value={addFileUrl}>
      <TiptapFileSrcProvider resolver={resolver}>{children}</TiptapFileSrcProvider>
    </AddFileUrlContext.Provider>
  );
}

/** Records the URL the files service returned for a just-uploaded file, so the editor can show it before the content is refetched. */
export function useAddFileUrl(): AddFileUrl {
  return useContext(AddFileUrlContext);
}

/**
 * Returns a resolver for tiptap file references that looks each `fileDrn` up in
 * `fileUrls`, the URL map the server returned beside the content. The browser
 * never builds a URL from a DRN.
 *
 * A DRN missing from the map falls through to the enclosing provider's resolver,
 * so a screen can layer its own query's map over a parent's. At the top the node
 * keeps its legacy `src`.
 *
 * The resolver reads the maps at call time, because an editor captures it once
 * when its extensions are configured. Its identity still changes with the map,
 * so node views reading it from context re-render with a re-signed map.
 */
export function useTiptapFileSrcResolver(
  fileUrls: Record<string, string> | undefined,
  uploadedFileUrlsRef?: { current: Record<string, string> },
): TiptapFileSrcResolver {
  const parent = useTiptapFileSrcResolverFromContext();
  const fileUrlsRef = useRef(fileUrls);
  fileUrlsRef.current = fileUrls;
  const parentRef = useRef(parent);
  parentRef.current = parent;

  return useMemo<TiptapFileSrcResolver>(
    () => (fileDrn, legacyUrl) => {
      const url =
        typeof fileDrn === 'string'
          ? (fileUrlsRef.current?.[fileDrn] ?? uploadedFileUrlsRef?.current[fileDrn])
          : undefined;
      return url ?? parentRef.current(fileDrn, legacyUrl);
    },
    [fileUrls, parent, uploadedFileUrlsRef],
  );
}
